import cn from 'classnames';
import { motion, useReducedMotion } from 'framer-motion';

const RATIO = 52 / 44;

/** The face on its own — for places where the bunny just sits there. */
export function BunnyFace({ size = 44, className }: { size?: number; className?: string }) {
    return (
        <svg viewBox="0 0 44 52" width={size} height={size * RATIO} className={className} aria-hidden="true">
            <ellipse cx="14" cy="14" rx="5" ry="13" fill="#E9E5E0" transform="rotate(-8 14 14)" />
            <ellipse cx="30" cy="14" rx="5" ry="13" fill="#E9E5E0" transform="rotate(8 30 14)" />
            <ellipse cx="14" cy="15" rx="2.2" ry="8.5" fill="var(--accent)" opacity="0.55" transform="rotate(-8 14 15)" />
            <ellipse cx="30" cy="15" rx="2.2" ry="8.5" fill="var(--accent)" opacity="0.55" transform="rotate(8 30 15)" />
            <ellipse cx="22" cy="38" rx="16" ry="14" fill="#E9E5E0" />
            <circle cx="16" cy="36" r="2.1" fill="#1C1B19" />
            <circle cx="28" cy="36" r="2.1" fill="#1C1B19" />
            <circle cx="16.7" cy="35.3" r="0.7" fill="#FFFFFF" />
            <circle cx="28.7" cy="35.3" r="0.7" fill="#FFFFFF" />
            <ellipse cx="11" cy="41" rx="2.6" ry="1.7" fill="var(--accent)" opacity="0.35" />
            <ellipse cx="33" cy="41" rx="2.6" ry="1.7" fill="var(--accent)" opacity="0.35" />
            <path d="M20.4 41.2h3.2L22 43z" fill="var(--accent)" />
            <path d="M22 43.2v1.4M22 44.6c-.9.9-2.4.9-3.2 0M22 44.6c.9.9 2.4.9 3.2 0" stroke="#1C1B19" strokeWidth="0.9" strokeLinecap="round" fill="none" />
        </svg>
    );
}

interface PeekingBunnyProps {
    /** What the speech bubble says. Omit for a silent bunny. */
    say?: string;
    size?: number;
    className?: string;
    /** Seconds of the cycle spent hidden before popping up again. */
    duration?: number;
}

/**
 * Pops its head up, says its line, and ducks back down — on a loop.
 * The mask is real overflow, so it rises from behind whatever line it sits on.
 */
export function PeekingBunny({ say, size = 44, className, duration = 9 }: PeekingBunnyProps) {
    const reduced = useReducedMotion();

    // A looping decoration is exactly what reduced motion opts out of.
    if (reduced) return null;

    const cycle = { duration, times: [0, 0.08, 0.14, 0.44, 0.52, 1], repeat: Infinity, ease: 'easeInOut' as const };

    return (
        <span className={cn('pointer-events-none select-none', className)} aria-hidden="true">
            {say && (
                <motion.span
                    className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-accent px-2.5 py-1 text-[12px] font-bold leading-none text-paper"
                    initial={{ opacity: 0, scale: 0.6, y: 6 }}
                    animate={{ opacity: [0, 0, 1, 1, 0, 0], scale: [0.6, 0.6, 1, 1, 0.6, 0.6], y: [6, 6, 0, 0, 6, 6] }}
                    transition={cycle}
                >
                    {say}
                    <span className="absolute -bottom-[3px] left-1/2 h-1.5 w-1.5 -translate-x-1/2 rotate-45 bg-accent" />
                </motion.span>
            )}

            <span className="block overflow-hidden" style={{ height: size * RATIO, width: size }}>
                <motion.span className="block" initial={{ y: '100%' }} animate={{ y: ['100%', '0%', '0%', '0%', '100%', '100%'] }} transition={cycle}>
                    <BunnyFace size={size} />
                </motion.span>
            </span>
        </span>
    );
}

interface IdleBunnyProps {
    /** Line shown in a bubble that pops every so often. Omit for a silent bunny. */
    say?: string;
    size?: number;
    className?: string;
    duration?: number;
}

/**
 * Always on screen, breathing gently, with an occasional line.
 * Use where the bunny is the point — hiding it would leave a hole in the copy.
 */
export function IdleBunny({ say, size = 40, className, duration = 7 }: IdleBunnyProps) {
    const reduced = useReducedMotion();

    return (
        <span className={cn('pointer-events-none relative select-none', className)} aria-hidden="true">
            {say && !reduced && (
                <motion.span
                    className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-accent px-2.5 py-1 text-[12px] font-bold leading-none text-paper"
                    initial={{ opacity: 0, scale: 0.6, y: 6 }}
                    animate={{ opacity: [0, 1, 1, 0, 0], scale: [0.6, 1, 1, 0.6, 0.6], y: [6, 0, 0, 6, 6] }}
                    transition={{ duration, times: [0, 0.08, 0.34, 0.42, 1], repeat: Infinity, ease: 'easeInOut' }}
                >
                    {say}
                    <span className="absolute -bottom-[3px] left-1/2 h-1.5 w-1.5 -translate-x-1/2 rotate-45 bg-accent" />
                </motion.span>
            )}

            <motion.span className="block" animate={reduced ? undefined : { y: [0, -3, 0] }} transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}>
                <BunnyFace size={size} />
            </motion.span>
        </span>
    );
}

/** Bunny hopping in place — shown while an image is still downloading. */
export function BunnyLoader({ size = 34 }: { size?: number }) {
    const reduced = useReducedMotion();

    return (
        <span className="pointer-events-none flex flex-col items-center gap-2" aria-hidden="true">
            <motion.span className="block" animate={reduced ? undefined : { y: [0, -7, 0] }} transition={{ duration: 0.9, repeat: Infinity, ease: 'easeInOut' }}>
                <BunnyFace size={size} />
            </motion.span>
            <motion.span className="block h-1 rounded-full bg-hairline" style={{ width: size * 0.6 }} animate={reduced ? undefined : { scaleX: [1, 0.6, 1], opacity: [0.5, 0.9, 0.5] }} transition={{ duration: 0.9, repeat: Infinity, ease: 'easeInOut' }} />
        </span>
    );
}

export default PeekingBunny;
